// Host invoicing: which trips a host can invoice, and the checks on the invoice
// form. The server (submit_host_invoice) enforces the same rules; these exist so
// the host hears about a mistake before uploading anything.

export type InvoiceState = 'due' | 'sent' | 'approved' | 'paid' | 'closed';

export interface PaymentFacts {
  status: string;
  payout_status: string;
}

/**
 * Where a completed trip stands on the money side.
 *   due      – the host can invoice it now
 *   sent     – on an invoice that Sekretly hasn't approved yet
 *   approved – the invoice is approved and waiting for the next payment run
 *   paid     – the host has been paid
 *   closed   – nothing to invoice (not paid by the traveler, or fully refunded)
 *
 * A partial refund doesn't change what the host is owed, so it doesn't close a trip.
 */
export function invoiceState(payment: PaymentFacts, invoice: { status: string } | null): InvoiceState {
  if (payment.status !== 'paid' || payment.payout_status === 'refunded') return 'closed';
  if (payment.payout_status === 'paid' || invoice?.status === 'paid') return 'paid';
  if (invoice?.status === 'approved') return 'approved';
  if (payment.payout_status === 'invoiced') return 'sent';
  if (payment.payout_status === 'held' || payment.payout_status === 'failed') return 'due';
  return 'closed';
}

export const INVOICE_STATE_LABEL: Record<InvoiceState, string> = {
  due: 'Invoice due',
  sent: 'Invoice sent',
  approved: 'Approved',
  paid: 'Paid',
  closed: '',
};

// ── Money ─────────────────────────────────────────────────────────────────────

/** "9,59", "9.59" and "9" all work; anything else is null. Empty means zero. */
export function parseMoneyToCents(text: string): number | null {
  const t = text.replace(/\s/g, '').replace(',', '.');
  if (t === '') return 0;
  if (!/^\d+(\.\d{1,2})?$/.test(t)) return null;
  return Math.round(parseFloat(t) * 100);
}

export function formatMoney(cents: number, currency = 'eur'): string {
  return new Intl.NumberFormat('en', { style: 'currency', currency: currency.toUpperCase() }).format(cents / 100);
}

/** The most VAT an invoice of this base amount can show: 23%, with a cent of slack for rounding. */
export function maxVatCents(baseCents: number): number {
  return Math.round(baseCents * 0.23) + 1;
}

// ── The invoice file ──────────────────────────────────────────────────────────

export const INVOICE_FILE_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];
export const INVOICE_FILE_MAX_BYTES = 5 * 1024 * 1024;

export function invoiceFileError(file: { type: string; size: number } | null): string | null {
  if (!file) return 'Attach your recibo as a PDF (or a photo of it).';
  if (!INVOICE_FILE_TYPES.includes(file.type)) return 'The file must be a PDF, JPG or PNG.';
  if (file.size <= 0) return 'This file is empty.';
  if (file.size > INVOICE_FILE_MAX_BYTES) return 'The file is too big — the limit is 5 MB.';
  return null;
}

/** A storage-safe version of the file's name, keeping its extension. */
export function safeFileName(fullName: string): string {
  const name = fullName.split(/[\\/]/).pop() ?? '';
  const dot = name.lastIndexOf('.');
  const ext = dot > 0 ? name.slice(dot + 1).toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 5) : '';
  const stem = (dot > 0 ? name.slice(0, dot) : name)
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 50) || 'invoice';
  return ext ? `${stem}.${ext}` : stem;
}

/** Files live in the host's own folder: <host id>/<unique id>-<name>. */
export function invoiceFilePath(hostId: string, fileName: string, uniqueId: string): string {
  return `${hostId}/${uniqueId}-${safeFileName(fileName)}`;
}

// ── The form ──────────────────────────────────────────────────────────────────

export interface InvoiceForm {
  kind: 'recibo_verde' | 'fatura';
  number: string;
  date: string; // YYYY-MM-DD
  vatMode: 'exempt' | 'vat';
  vat: string;
  exemption: string;
  withholding: string;
  file: { type: string; size: number } | null;
}

export type InvoiceFormErrors = Partial<Record<'number' | 'date' | 'vat' | 'exemption' | 'withholding' | 'file', string>>;

export interface InvoiceAmounts {
  totalCents: number;
  vatCents: number;
  baseCents: number;
  withholdingCents: number;
  payableCents: number;
}

/** What the invoice must show, worked out from the trips and what the host typed. */
export function invoiceAmounts(totalCents: number, form: Pick<InvoiceForm, 'vatMode' | 'vat' | 'withholding'>): InvoiceAmounts | null {
  const vat = form.vatMode === 'vat' ? parseMoneyToCents(form.vat) : 0;
  const withholding = parseMoneyToCents(form.withholding);
  if (vat === null || withholding === null) return null;
  const base = totalCents - vat;
  return { totalCents, vatCents: vat, baseCents: base, withholdingCents: withholding, payableCents: totalCents - withholding };
}

export function invoiceFormErrors(form: InvoiceForm, totalCents: number, today: string): InvoiceFormErrors {
  const errors: InvoiceFormErrors = {};
  const number = form.number.trim();
  if (number.length < 1) errors.number = 'Enter the invoice number.';
  else if (number.length > 40) errors.number = 'The invoice number is too long.';

  if (!/^\d{4}-\d{2}-\d{2}$/.test(form.date) || form.date < '2020-01-01') errors.date = 'Enter the date on your invoice.';
  else if (form.date > today) errors.date = 'The invoice date can’t be in the future.';

  const amounts = invoiceAmounts(totalCents, form);
  if (form.vatMode === 'vat') {
    const vat = parseMoneyToCents(form.vat);
    if (vat === null || vat <= 0) errors.vat = 'Enter the VAT amount shown on your invoice.';
    else if (vat >= totalCents) errors.vat = 'The VAT can’t be the whole amount.';
    else if (vat > maxVatCents(totalCents - vat)) errors.vat = 'That VAT is more than 23% of the base amount.';
  } else if (form.exemption.trim().length < 3) {
    errors.exemption = 'Say why there is no VAT, for example “Isento art. 53.º CIVA”.';
  }

  if (amounts === null) {
    if (parseMoneyToCents(form.withholding) === null) errors.withholding = 'Enter an amount like 12.50, or leave it empty.';
  } else if (amounts.withholdingCents > amounts.baseCents) {
    errors.withholding = 'Tax withheld can’t be more than the base amount.';
  }

  const fileError = invoiceFileError(form.file);
  if (fileError) errors.file = fileError;
  return errors;
}

// ── Invoice history ───────────────────────────────────────────────────────────

export interface InvoiceStatusFacts {
  status: string;
  rejected_reason: string | null;
  paid_at: string | null;
  payment_reference: string | null;
}

/** What to tell the host about an invoice they have sent. */
export function invoiceStatusLabel(inv: InvoiceStatusFacts): { label: string; tone: 'wait' | 'good' | 'bad' } {
  switch (inv.status) {
    case 'approved':
      return { label: 'Approved · paid in the next payment run', tone: 'good' };
    case 'paid': {
      const when = inv.paid_at ? new Date(inv.paid_at).toLocaleDateString('en', { day: 'numeric', month: 'short', year: 'numeric' }) : '';
      const ref = inv.payment_reference ? ` · ref. ${inv.payment_reference}` : '';
      return { label: `Paid${when ? ` on ${when}` : ''}${ref}`, tone: 'good' };
    }
    case 'rejected':
      return { label: `Sent back${inv.rejected_reason ? `: ${inv.rejected_reason}` : ''}`, tone: 'bad' };
    default:
      return { label: 'Waiting for our review', tone: 'wait' };
  }
}
